import { useMutation, useQuery } from '@apollo/client/react';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Card,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import { StatusBadge } from '../components/StatusBadge';
import type { Plan, UserRole } from '../gql/graphql';
import {
  AdminStoriesDocument,
  AdminUpdateUserDocument,
  AdminUserDocument,
} from '../graphql/operations';
import { PLANS, shortId, useLabels, USER_ROLES } from '../labels';

export function UserDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = useLabels();

  const { data, loading, error } = useQuery(AdminUserDocument, { variables: { id } });
  const [updateUser, { loading: saving }] = useMutation(AdminUpdateUserDocument);

  // The user's own stories, newest first. A separate query rather than a field
  // on the user: it is paginated on the server and this page shows one page.
  const stories = useQuery(AdminStoriesDocument, {
    variables: { userId: id, limit: 20, offset: 0 },
    skip: !data,
  });

  async function save(input: { plan?: Plan; role?: UserRole }) {
    try {
      await updateUser({ variables: { id, input } });
      notifications.show({ color: 'teal', message: t('common.saved') });
    } catch (thrown) {
      notifications.show({
        color: 'red',
        title: t('common.saveFailed'),
        message: toApiError(thrown as Error)?.message ?? '',
      });
    }
  }

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/users">
          {t('users.title')}
        </Anchor>
        <Text>{data?.adminUser.email ?? shortId(id)}</Text>
      </Breadcrumbs>

      <QueryState loading={loading && !data} error={error}>
        {data && (
          <>
            <Title order={2}>{data.adminUser.email ?? t('users.detail.noEmail')}</Title>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Field label={t('common.id')} value={data.adminUser.id} />
                  <Field
                    label={t('users.detail.provider')}
                    value={data.adminUser.providers
                      .map((provider) => labels.provider(provider))
                      .join(', ')}
                  />
                  <Field
                    label={t('users.detail.locale')}
                    value={labels.locale(data.adminUser.locale)}
                  />
                  <Field
                    label={t('users.detail.stories')}
                    value={String(data.adminUser.storiesCount)}
                  />
                  <Field
                    label={t('users.detail.createdAt')}
                    value={labels.dateTime(data.adminUser.createdAt)}
                  />
                </Stack>
              </Card>

              <Card withBorder padding="md">
                <Stack gap="sm">
                  <Text fw={600}>{t('users.detail.rights')}</Text>
                  <Select
                    label={t('users.detail.plan')}
                    allowDeselect={false}
                    disabled={saving}
                    data={PLANS.map((value) => ({ value, label: labels.plan(value) }))}
                    value={data.adminUser.plan}
                    onChange={(value) => {
                      if (value) void save({ plan: value as Plan });
                    }}
                  />
                  <Select
                    label={t('users.detail.role')}
                    allowDeselect={false}
                    disabled={saving}
                    data={USER_ROLES.map((value) => ({ value, label: labels.role(value) }))}
                    value={data.adminUser.role}
                    onChange={(value) => {
                      if (value) void save({ role: value as UserRole });
                    }}
                  />
                  <Text size="xs" c="dimmed">
                    {t('users.detail.billingNote')}
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>

            <Title order={4}>{t('users.detail.storiesTitle')}</Title>
            <QueryState loading={stories.loading && !stories.data} error={stories.error}>
              {stories.data && (
                <Table.ScrollContainer minWidth={600}>
                  <Table highlightOnHover striped>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('common.id')}</Table.Th>
                        <Table.Th>{t('stories.columns.mode')}</Table.Th>
                        <Table.Th>{t('stories.columns.status')}</Table.Th>
                        <Table.Th>{t('stories.columns.title')}</Table.Th>
                        <Table.Th>{t('stories.columns.locale')}</Table.Th>
                        <Table.Th>{t('stories.columns.createdAt')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {stories.data.adminStories.items.map((story) => (
                        <Table.Tr
                          key={story.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => void navigate(`/stories/${story.id}`)}
                        >
                          <Table.Td>
                            <Badge variant="default" size="sm">
                              {shortId(story.id)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{labels.storyMode(story.mode)}</Table.Td>
                          <Table.Td>
                            <StatusBadge status={story.status} />
                          </Table.Td>
                          <Table.Td>
                            {story.title ?? <Text c="dimmed">{t('common.empty')}</Text>}
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="default" size="sm">
                              {story.locale.toUpperCase()}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{labels.dateTime(story.createdAt)}</Table.Td>
                        </Table.Tr>
                      ))}
                      {stories.data.adminStories.items.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={6}>
                            <Text c="dimmed" ta="center" py="md">
                              {t('users.detail.noStories')}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </QueryState>
          </>
        )}
      </QueryState>
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="xl" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" style={{ wordBreak: 'break-all' }} ta="right">
        {value}
      </Text>
    </Group>
  );
}
